import { computed, readonly, ref } from 'vue';

export interface UseAsyncQueueOptions {
  /**
   * Whether to execute queued operations immediately when queue becomes available
   * @default true
   */
  autoFlush?: boolean;
}

export function useAsyncQueue(options: UseAsyncQueueOptions = {}) {
  const { autoFlush = true } = options;

  const isProcessing = ref(false);
  const queue = ref<(() => Promise<any>)[]>([]);

  /**
   * Add an operation to the queue. If not currently processing, execute immediately.
   * Otherwise, add to queue and execute when current operation completes.
   */
  const enqueue = async (fn?: () => Promise<any>) => {
    if (isProcessing.value) {
      if (fn) {
        queue.value.unshift(fn);
      }

      return;
    }

    isProcessing.value = true;

    try {
      if (fn) {
        await fn();
      }
    } finally {
      isProcessing.value = false;

      if (autoFlush) {
        await flush();
      }
    }
  };

  /**
   * Process all queued operations in LIFO order (Last In, First Out)
   */
  const flush = async () => {
    while (queue.value.length > 0) {
      const fn = queue.value.pop();
      if (fn) {
        await fn();
      }
    }
  };

  /**
   * Clear all queued operations without executing them
   */
  const clear = () => {
    queue.value = [];
  };

  /**
   * Get the current queue size
   */
  const queueSize = computed(() => queue.value.length);

  return {
    isProcessing: readonly(isProcessing),
    queueSize,
    enqueue,
    flush,
    clear,
  };
}
